import type { TSolidMeshSavedData } from "earthbrain-viewer";
import { ObjectType } from "../types";
import type {
	IBoxObjectMetaData,
	IBoxObjectMetaData_V1,
	IBoxObjectMetaData_V2,
	ICylinderObjectMetaData,
	ICylinderObjectMetaData_V1,
	ICylinderObjectMetaData_V2,
	IShapeObjectMetaData,
	ISolidMeshObjectData,
	ISphereObjectMetaData,
	ISphereObjectMetaData_V1,
	ISphereObjectMetaData_V2,
	TBoxVersioned,
	TClientAttributes,
	TCylinderVersioned,
	TSimpleStructureType,
	TSphereVersioned,
} from "./types";

export const BOX_SOLID_MESH_LATEST_VERSION = 2.0;
export const SPHERE_SOLID_MESH_LATEST_VERSION = 2.0;
export const CYLINDER_SOLID_MESH_LATEST_VERSION = 2.0;

/** Union of all versioned metaData types */
export type TSolidMeshVersionedMetaData =
	| TBoxVersioned
	| TSphereVersioned
	| TCylinderVersioned;

/**
 * Adapter for SolidMesh data conversion
 * Converts versioned metaData from server to ISolidMeshObjectData
 */
// biome-ignore lint/complexity/noStaticOnlyClass: Adapter pattern
export class SolidMeshObjectAdapter {
	/**
	 * Convert versioned metaData to ISolidMeshObjectData
	 * @param data - Versioned metaData from server
	 * @param id - Top-level id from server (not stored in metaData)
	 * @param name - Top-level name from server (not stored in metaData)
	 */
	static toSolidMeshData(
		data: TSolidMeshVersionedMetaData,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		const shapeType = data.shapeConfig.shapeType;

		switch (shapeType) {
			case "box":
				return SolidMeshObjectAdapter.convertBox(
					data as TBoxVersioned,
					id,
					name,
				);
			case "sphere":
				return SolidMeshObjectAdapter.convertSphere(
					data as TSphereVersioned,
					id,
					name,
				);
			case "cylinder":
				return SolidMeshObjectAdapter.convertCylinder(
					data as TCylinderVersioned,
					id,
					name,
				);
			default: {
				throw new Error(`Unknown shapeType: ${shapeType}`);
			}
		}
	}

	private static convertBox(
		data: TBoxVersioned,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		if (data.version >= 2.0) {
			return SolidMeshObjectAdapter.fromBoxMetaData_V2(
				data as IBoxObjectMetaData_V2,
				id,
				name,
			);
		}
		return SolidMeshObjectAdapter.fromBoxMetaData_V1(
			data as IBoxObjectMetaData_V1,
			id,
			name,
		);
	}

	private static fromBoxMetaData_V1(
		data: IBoxObjectMetaData_V1,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		// V1→V2 migration: strip rotation (now viewer in-memory baseline)
		const { rotation: _, ...rest } = data.attributes;
		const metaData: IBoxObjectMetaData = {
			shapeConfig: data.shapeConfig,
			attributes: rest,
		};
		return {
			type: ObjectType.SOLID_MESH,
			id,
			md5: "",
			name,
			associatedTerrain: { id: 0, terrainType: "" },
			metaData,
		};
	}

	private static fromBoxMetaData_V2(
		data: IBoxObjectMetaData_V2,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		const metaData: IBoxObjectMetaData = {
			shapeConfig: data.shapeConfig,
			attributes: data.attributes,
		};
		return {
			type: ObjectType.SOLID_MESH,
			id,
			md5: "",
			name,
			associatedTerrain: { id: 0, terrainType: "" },
			metaData,
		};
	}

	private static convertSphere(
		data: TSphereVersioned,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		if (data.version >= 2.0) {
			return SolidMeshObjectAdapter.fromSphereMetaData_V2(
				data as ISphereObjectMetaData_V2,
				id,
				name,
			);
		}
		return SolidMeshObjectAdapter.fromSphereMetaData_V1(
			data as ISphereObjectMetaData_V1,
			id,
			name,
		);
	}

	private static fromSphereMetaData_V1(
		data: ISphereObjectMetaData_V1,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		const { rotation: _, ...rest } = data.attributes;
		const metaData: ISphereObjectMetaData = {
			shapeConfig: data.shapeConfig,
			attributes: rest,
		};
		return {
			type: ObjectType.SOLID_MESH,
			id,
			md5: "",
			name,
			associatedTerrain: { id: 0, terrainType: "" },
			metaData,
		};
	}

	private static fromSphereMetaData_V2(
		data: ISphereObjectMetaData_V2,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		const metaData: ISphereObjectMetaData = {
			shapeConfig: data.shapeConfig,
			attributes: data.attributes,
		};
		return {
			type: ObjectType.SOLID_MESH,
			id,
			md5: "",
			name,
			associatedTerrain: { id: 0, terrainType: "" },
			metaData,
		};
	}

	private static convertCylinder(
		data: TCylinderVersioned,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		if (data.version >= 2.0) {
			return SolidMeshObjectAdapter.fromCylinderMetaData_V2(
				data as ICylinderObjectMetaData_V2,
				id,
				name,
			);
		}
		return SolidMeshObjectAdapter.fromCylinderMetaData_V1(
			data as ICylinderObjectMetaData_V1,
			id,
			name,
		);
	}

	private static fromCylinderMetaData_V1(
		data: ICylinderObjectMetaData_V1,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		const { rotation: _, ...rest } = data.attributes;
		const metaData: ICylinderObjectMetaData = {
			shapeConfig: data.shapeConfig,
			attributes: rest,
		};
		return {
			type: ObjectType.SOLID_MESH,
			id,
			md5: "",
			name,
			associatedTerrain: { id: 0, terrainType: "" },
			metaData,
		};
	}

	private static fromCylinderMetaData_V2(
		data: ICylinderObjectMetaData_V2,
		id: string,
		name: string,
	): ISolidMeshObjectData {
		const metaData: ICylinderObjectMetaData = {
			shapeConfig: data.shapeConfig,
			attributes: data.attributes,
		};
		return {
			type: ObjectType.SOLID_MESH,
			id,
			md5: "",
			name,
			associatedTerrain: { id: 0, terrainType: "" },
			metaData,
		};
	}

	// ============================================
	// Viewer SavedData to MetaData conversion
	// ============================================

	/**
	 * Convert SavedData to MetaData
	 * Note: id and name are NOT stored in metaData, only in top-level ObjectData
	 */
	static savedDataToObjectMetaData(
		savedData: TSolidMeshSavedData,
		options?: {
			structureType?: TSimpleStructureType;
		},
	): IShapeObjectMetaData {
		const attributes: TClientAttributes = {
			anchorType: savedData.attributes.anchorType,
			structureType: options?.structureType,
			...(savedData.attributes?.baselineQuaternion
				? {
						baselineQuaternion: {
							x: savedData.attributes.baselineQuaternion.x,
							y: savedData.attributes.baselineQuaternion.y,
							z: savedData.attributes.baselineQuaternion.z,
							w: savedData.attributes.baselineQuaternion.w,
						},
					}
				: {}),
			...(savedData.attributes?.bottomZ !== undefined
				? { bottomZ: savedData.attributes?.bottomZ }
				: {}),
		};

		switch (savedData.shapeConfig.shapeType) {
			case "box":
				return {
					shapeConfig: savedData.shapeConfig,
					attributes,
				} satisfies IBoxObjectMetaData;
			case "sphere":
				return {
					shapeConfig: savedData.shapeConfig,
					attributes,
				} satisfies ISphereObjectMetaData;
			case "cylinder":
				return {
					shapeConfig: savedData.shapeConfig,
					attributes,
				} satisfies ICylinderObjectMetaData;
		}
	}
}
